import { useEffect, useState } from "react";
import AdminLayout from "../components/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Plus, Edit, Trash2, Search, Filter } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../App";

export default function AdminServers() {
  const [servers, setServers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editingServer, setEditingServer] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [formData, setFormData] = useState({
    name: "",
    ip_address: "",
    location: "",
    status: "Active",
    description: "",
  });

  useEffect(() => {
    fetchServers();
  }, []);

  const fetchServers = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/servers`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setServers(response.data);
    } catch (error) {
      toast.error("Failed to fetch servers");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem("token");
      if (editingServer) {
        await axios.put(`${API}/servers/${editingServer.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Server updated successfully");
      } else {
        await axios.post(`${API}/servers`, formData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Server created successfully");
      }
      setOpen(false);
      resetForm();
      fetchServers();
    } catch (error) {
      toast.error("Failed to save server");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this server?")) return;
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/servers/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Server deleted successfully");
      fetchServers();
    } catch (error) {
      toast.error("Failed to delete server");
    }
  };

  const openEditDialog = (server) => {
    setEditingServer(server);
    setFormData({
      name: server.name,
      ip_address: server.ip_address,
      location: server.location,
      status: server.status,
      description: server.description || "",
    });
    setOpen(true);
  };

  const resetForm = () => {
    setEditingServer(null);
    setFormData({
      name: "",
      ip_address: "",
      location: "",
      status: "Active",
      description: "",
    });
  };

  const getStatusBadge = (status) => {
    const colors = {
      Active: "bg-emerald-100 text-emerald-700 border-emerald-300",
      Maintenance: "bg-amber-100 text-amber-700 border-amber-300",
      Down: "bg-red-100 text-red-700 border-red-300",
    };
    return (
      <span
        className={`px-3 py-1 text-xs font-semibold border rounded-full ${colors[status] || colors.Active}`}
      >
        {status}
      </span>
    );
  };

  const filteredServers = servers.filter((server) => {
    const matchesSearch =
      server.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      server.ip_address.includes(searchQuery) ||
      server.location.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesStatus =
      statusFilter === "all" || server.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-screen">
          Loading...
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 mb-2">
              Server Management
            </h1>
            <p className="text-slate-600">Manage your infrastructure servers</p>
          </div>
          <Dialog
            open={open}
            onOpenChange={(val) => {
              setOpen(val);
              if (!val) resetForm();
            }}
          >
            <DialogTrigger asChild>
              <Button
                className="bg-blue-600 hover:bg-blue-700 text-white gap-2 rounded-lg shadow-lg shadow-blue-600/30"
                data-testid="add-server-btn"
              >
                <Plus className="w-4 h-4" />
                Add Server
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[500px] p-0 overflow-hidden">
              <DialogHeader className="top-0 z-20 bg-white px-6 py-4 border-b">
                <DialogTitle className="text-xl font-bold">
                  {editingServer ? "Edit Server" : "Add New Server"}
                </DialogTitle>
              </DialogHeader>
              <div className="px-6 pb-6">
                <form
                  onSubmit={handleSubmit}
                  className="space-y-4"
                  data-testid="server-form"
                >
                  <div className="relative">
                    {loading && (
                      <div className="absolute inset-0 bg-white/70 backdrop-blur-sm z-50 flex items-center justify-center rounded-lg">
                        <div className="flex items-center gap-2 text-blue-700 font-semibold">
                          <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                          Saving client...
                        </div>
                      </div>
                    )}
                    <div>
                      <Label className="text-sm font-medium text-slate-700">
                        Server Name
                      </Label>
                      <Input
                        data-testid="server-name-input"
                        value={formData.name}
                        onChange={(e) =>
                          setFormData({ ...formData, name: e.target.value })
                        }
                        className="mt-1.5 rounded-lg"
                        required
                      />
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-slate-700">
                        IP Address
                      </Label>
                      <Input
                        data-testid="server-ip-input"
                        value={formData.ip_address}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            ip_address: e.target.value,
                          })
                        }
                        className="mt-1.5 rounded-lg"
                        required
                      />
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-slate-700">
                        Location
                      </Label>
                      <Input
                        data-testid="server-location-input"
                        value={formData.location}
                        onChange={(e) =>
                          setFormData({ ...formData, location: e.target.value })
                        }
                        className="mt-1.5 rounded-lg"
                        required
                      />
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-slate-700">
                        Status
                      </Label>
                      <Select
                        value={formData.status}
                        onValueChange={(val) =>
                          setFormData({ ...formData, status: val })
                        }
                      >
                        <SelectTrigger
                          data-testid="server-status-select"
                          className="mt-1.5 rounded-lg"
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Active">Active</SelectItem>
                          <SelectItem value="Maintenance">
                            Maintenance
                          </SelectItem>
                          <SelectItem value="Down">Down</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-slate-700">
                        Description
                      </Label>
                      <Input
                        data-testid="server-description-input"
                        value={formData.description}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            description: e.target.value,
                          })
                        }
                        className="mt-1.5 rounded-lg"
                      />
                    </div>
                    <Button
                      type="submit"
                      className="w-full bg-blue-600 hover:bg-blue-700 text-[20px] font-semibold h-14 mt-4"
                      data-testid="server-submit-btn"
                    >
                      {editingServer ? "Update Server" : "Create Server"}
                    </Button>
                  </div>
                </form>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Search and Filter Bar */}
        <div className="mb-6 flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
            <Input
              placeholder="Search servers..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 rounded-lg bg-white border-slate-200"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[180px] rounded-lg bg-white">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="Active">Active</SelectItem>
              <SelectItem value="Maintenance">Maintenance</SelectItem>
              <SelectItem value="Down">Down</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="servers-table">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    IP Address
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Location
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Area List
                  </th>
                  <th className="px-6 py-4 text-right text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredServers.length === 0 ? (
                  <tr>
                    <td
                      colSpan="5"
                      className="px-6 py-12 text-center text-slate-500"
                    >
                      {searchQuery || statusFilter !== "all"
                        ? "No servers match your filters"
                        : "No servers yet. Add your first server!"}
                    </td>
                  </tr>
                ) : (
                  filteredServers.map((server) => (
                    <tr
                      key={server.id}
                      data-testid={`server-row-${server.id}`}
                      className="hover:bg-slate-50 transition-colors"
                    >
                      <td className="px-6 py-4">
                        <p className="text-sm font-semibold text-slate-900">
                          {server.name}
                        </p>
                        {server.description && (
                          <p className="text-xs text-slate-500 mt-0.5">
                            {server.description}
                          </p>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-600 font-mono">
                        {server.ip_address}
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-600">
                        {server.location}
                      </td>
                      <td className="px-6 py-4">
                        {getStatusBadge(server.status)}
                      </td>
                      <td className="px-6 py-4">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick="/area-list"
                          className="bg-blue-900 text-white hover:bg-blue-600 hover:text-white rounded-lg"
                          data-testid={`add-area-btn-${server.id}`}
                        >
                          Area List
                        </Button>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex gap-2 justify-end">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => openEditDialog(server)}
                            className="hover:bg-blue-50 hover:text-blue-600 rounded-lg"
                            data-testid={`edit-server-${server.id}`}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDelete(server.id)}
                            className="hover:bg-red-50 hover:text-red-600 rounded-lg"
                            data-testid={`delete-server-${server.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Results count */}
        <div className="mt-4 text-sm text-slate-600">
          Showing {filteredServers.length} of {servers.length} servers
        </div>
      </div>
    </AdminLayout>
  );
}
